/**
 * Audit UI — Evidence-Driven 결과 카드 (Frontend A, Perplexity 풍).
 *
 * Props:
 *   - response: /classify-v2 응답 객체 (ClassifyV2Response)
 *   - apiBase: 백엔드 URL (기본: window.location.origin)
 *   - onDownloadPdf?: () => void   // PDF 다운로드 트리거 (호출자가 구현)
 */
import React, { useState } from "react";
import {
  FileText,
  BookOpen,
  Scale,
  Download,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import "./theme.css";

function ConfidenceBar({ level }) {
  const filled = level === "high" ? 5 : level === "medium" ? 3 : 1;
  return (
    <div className="audit-conf-bar" title={level}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          className={`audit-conf-cell ${i <= filled ? (level === "medium" ? "on-mid" : "on") : ""}`}
        />
      ))}
      <span
        style={{
          marginLeft: 8,
          fontSize: 12,
          color: "var(--audit-text-muted)",
          textTransform: "uppercase",
        }}
      >
        {level}
      </span>
    </div>
  );
}

function DecisionCard({ resp }) {
  return (
    <div className="audit-decision">
      <div className="audit-decision-hs10">
        {resp.hs_10digit || resp.hs_6digit || "(미확정)"}
      </div>
      <div className="audit-decision-desc">
        {resp.description_ko || "(품명 미확정)"}
      </div>
      <div className="audit-decision-meta">
        <ConfidenceBar level={resp.confidence || "low"} />
        <span style={{ flex: 1 }} />
        <span className="audit-routing" title="routing reasons">
          {resp.engine === "dual_evidence" ? "Audit" : "Fast"} · {resp.chapter}
          류
        </span>
      </div>
      {resp.audit_evidence?.requires_human_review && (
        <div
          style={{
            marginTop: 8,
            padding: 8,
            borderLeft: "2px solid var(--audit-accent)",
            background: "var(--audit-accent-bg)",
            fontSize: 12,
            color: "var(--audit-text-muted)",
          }}
        >
          ⓘ 검토 필요 —{" "}
          {resp.audit_evidence.review_reason || "관세사 추가 검토 권장"}
        </div>
      )}
    </div>
  );
}

function EvidenceItem({ title, meta, body }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`audit-evidence-item ${open ? "expanded" : ""}`}
      onClick={() => setOpen(!open)}
    >
      <div className="audit-evidence-item-title">
        {title}
        <span style={{ float: "right", color: "var(--audit-text-faint)" }}>
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </div>
      {meta && <div className="audit-evidence-item-meta">{meta}</div>}
      {body && <div className="audit-evidence-item-body">{body}</div>}
    </div>
  );
}

function LawSection({ ev }) {
  if (!ev) return null;
  const gris = ev.applied_gri || [];
  return (
    <div className="audit-evidence-section">
      <h3>
        <Scale
          size={12}
          style={{ display: "inline", verticalAlign: "text-top" }}
        />{" "}
        법령 근거
      </h3>
      {gris.length > 0 &&
        gris.map((gri) => (
          <EvidenceItem
            key={gri}
            title={gri}
            meta="General Interpretive Rule"
          />
        ))}
      {ev.legal_reasoning && (
        <EvidenceItem
          title="법령 인용 (호의 용어 + 부주/류주)"
          meta=""
          body={ev.legal_reasoning}
        />
      )}
      {!ev.legal_reasoning && gris.length === 0 && (
        <div className="audit-empty">법령 근거 없음</div>
      )}
    </div>
  );
}

function PrecedentSection({ ev }) {
  if (!ev) return null;
  const cases = ev.precedent_cases || [];
  return (
    <div className="audit-evidence-section">
      <h3>
        <BookOpen
          size={12}
          style={{ display: "inline", verticalAlign: "text-top" }}
        />{" "}
        선례
        <span
          style={{
            fontSize: 12,
            color: "var(--audit-text-faint)",
            marginLeft: 6,
            textTransform: "none",
            letterSpacing: 0,
            fontWeight: "normal",
          }}
        >
          ({ev.precedent_agreement || "no_precedent"})
        </span>
      </h3>
      {cases.length === 0 ? (
        <div className="audit-empty">
          본 분류는 법령 근거에 의함. 사전심사 선례는 본 도메인에서 아직
          색인되지 않았습니다.
        </div>
      ) : (
        cases.map((p, i) => (
          <EvidenceItem
            key={p.case_id || i}
            title={p.case_id || `사례 #${i + 1}`}
            meta={`${p.decision_date || "—"} · HS ${p.decided_hs_code_6digit || "—"}`}
            body={(p.key_reasoning_excerpt || "").slice(0, 500)}
          />
        ))
      )}
    </div>
  );
}

export default function EvidenceCards({
  response,
  onDownloadPdf,
  onOpenPrecedents,
}) {
  if (!response) return null;
  const ev = response.audit_evidence;
  const isAudit = response.routing?.path === "audit";

  return (
    <div className="audit-root" style={{ padding: 16 }}>
      <DecisionCard resp={response} />

      {isAudit && ev ? (
        <div className="audit-evidence">
          <LawSection ev={ev} />
          <PrecedentSection ev={ev} />
        </div>
      ) : (
        <div className="audit-card-flat" style={{ marginTop: 12 }}>
          <div className="audit-evidence-item-meta">
            Fast path 응답 — audit evidence 없음
          </div>
          <div style={{ marginTop: 8, fontSize: 13 }}>
            {response.reasoning || "(reasoning 없음)"}
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 16,
          justifyContent: "flex-end",
        }}
      >
        {onOpenPrecedents && (
          <button className="audit-btn" onClick={onOpenPrecedents}>
            <FileText size={14} /> 전체 선례 검색
          </button>
        )}
        {onDownloadPdf && isAudit && (
          <button
            className="audit-btn audit-btn-primary"
            onClick={onDownloadPdf}
          >
            <Download size={14} /> 컨펌 PDF 다운로드
          </button>
        )}
      </div>
    </div>
  );
}
